import { createServerFn } from "@tanstack/react-start";
import type {
  ListServiceRequestsApiWorkspacesWorkspaceIdServiceRequestsGetParams,
  ServiceRequestCreateRequest,
} from "@/lib/api/generated";
import {
  createServiceRequestApiWorkspacesWorkspaceIdServiceRequestsPost,
  getServiceRequestApiWorkspacesWorkspaceIdServiceRequestsRequestIdGet,
  listServiceRequestsApiWorkspacesWorkspaceIdServiceRequestsGet,
  listServiceRequestTimelineApiWorkspacesWorkspaceIdServiceRequestsRequestIdTimelineGet,
} from "@/lib/api/generated/service-marketplace/service-marketplace";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError, logInfo } from "@/lib/structured-log";

export const listServiceRequestsServer = createServerFn({ method: "GET" })
  .validator(
    (
      data: {
        workspaceId: string;
      } & ListServiceRequestsApiWorkspacesWorkspaceIdServiceRequestsGetParams,
    ) => data,
  )
  .handler(async ({ data }) => {
    const { workspaceId, ...params } = data;
    logInfo("service_marketplace.requests.list.server_fn_called", {
      workspace_id: workspaceId,
      status: params.status,
      service_type: params.service_type,
    });
    const response = await listServiceRequestsApiWorkspacesWorkspaceIdServiceRequestsGet(
      workspaceId,
      params,
    );
    if (response.status !== 200) {
      logError("service_marketplace.requests.list.failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to list service requests"));
    }
    return response.data as unknown;
  });

export const createServiceRequestServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; payload: ServiceRequestCreateRequest }) => data)
  .handler(async ({ data }) => {
    logInfo("service_marketplace.requests.create.server_fn_called", {
      workspace_id: data.workspaceId,
      service_type: data.payload.service_type,
    });
    const response = await createServiceRequestApiWorkspacesWorkspaceIdServiceRequestsPost(
      data.workspaceId,
      data.payload,
    );
    if (response.status !== 201) {
      logError("service_marketplace.requests.create.failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to create service request"));
    }
    return response.data as unknown;
  });

export const getServiceRequestServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; requestId: string }) => data)
  .handler(async ({ data }) => {
    const response = await getServiceRequestApiWorkspacesWorkspaceIdServiceRequestsRequestIdGet(
      data.workspaceId,
      data.requestId,
    );
    if (response.status !== 200) {
      logError("service_marketplace.requests.get.failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to get service request"));
    }
    return response.data as unknown;
  });

export const listServiceRequestTimelineServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string; requestId: string }) => data)
  .handler(async ({ data }) => {
    const response =
      await listServiceRequestTimelineApiWorkspacesWorkspaceIdServiceRequestsRequestIdTimelineGet(
        data.workspaceId,
        data.requestId,
      );
    if (response.status !== 200) {
      logError("service_marketplace.requests.timeline.failed", { status: response.status });
      throw new Error(extractApiErrorMessage(response.data, "Failed to get service timeline"));
    }
    return response.data as unknown;
  });
